import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

function tokenExpiryMs(token) {
  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return Number(payload.exp) * 1000;
  } catch {
    return 0;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);
  const expiryTimer = useRef(null);

  const clearSession = useCallback((showMessage = false) => {
    localStorage.removeItem("aureon_token");
    if (expiryTimer.current) window.clearTimeout(expiryTimer.current);
    setUser(false);
    if (showMessage) toast.info("Your admin session expired. Please sign in again.");
  }, []);

  const scheduleExpiry = useCallback((token) => {
    if (expiryTimer.current) window.clearTimeout(expiryTimer.current);
    const remaining = tokenExpiryMs(token) - Date.now();
    if (remaining <= 0) {
      clearSession(true);
      return false;
    }
    expiryTimer.current = window.setTimeout(() => clearSession(true), Math.min(remaining, 2_147_000_000));
    return true;
  }, [clearSession]);

  useEffect(() => {
    const onExpired = () => clearSession(true);
    window.addEventListener("aureon:auth-expired", onExpired);
    const token = localStorage.getItem("aureon_token");
    if (!token || !scheduleExpiry(token)) {
      setUser(false);
      setReady(true);
    } else {
      api
        .get("/auth/me")
        .then((response) => setUser(response.data))
        .catch(() => clearSession(false))
        .finally(() => setReady(true));
    }
    return () => {
      window.removeEventListener("aureon:auth-expired", onExpired);
      if (expiryTimer.current) window.clearTimeout(expiryTimer.current);
    };
  }, [clearSession, scheduleExpiry]);

  const login = async (email, password, otpCode = "") => {
    const { data } = await api.post("/auth/login", { email, password, otp_code: otpCode || null });
    localStorage.setItem("aureon_token", data.token);
    scheduleExpiry(data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // The local session must still be cleared if the server is unavailable.
    } finally {
      clearSession(false);
    }
  };

  return <AuthContext.Provider value={{ user, ready, login, logout }}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
